import React, { useEffect, useMemo, useState } from "react";
import { collection, addDoc, serverTimestamp, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebaseConfig";

const norm = (s) => String(s || "").toLowerCase().trim();
const stockTxt = (s) => (typeof s === "number" ? (s < 0 ? "Non géré" : s) : "—");

// Chez toi variantKey = size (40,41,42…)
const getVariantKey = (v) => String(v?.size || "default");

const variantLabel = (v) => {
  const size = v?.size ? `Taille ${v.size}` : "—";
  const sku = v?.sku ? `SKU ${v.sku}` : null;
  const stock = typeof v?.stock === "number" ? `stock ${stockTxt(v.stock)}` : null;
  return [size, sku, stock].filter(Boolean).join(" • ");
};

export default function ReceptionCreate() {
  const [products, setProducts] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");

  const [items, setItems] = useState([{ productId: "", variantKey: "default", qty: 1, unitCost: "" }]);
  const [saving, setSaving] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);

  // Recherche qui remplit la ligne
  const [searchText, setSearchText] = useState("");
  const [activeRow, setActiveRow] = useState(0);
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        setLoadingProducts(true);
        const snap = await getDocs(query(collection(db, "products"), orderBy("title", "asc")));
        setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } finally {
        setLoadingProducts(false);
      }
    })();
  }, []);

  const productMap = useMemo(() => {
    const m = new Map();
    products.forEach((p) => m.set(p.id, p));
    return m;
  }, [products]);

  // ✅ Variantes basées sur size/sku/stock (dédoublonné + tri)
  const getVariants = (productId) => {
    const p = productMap.get(productId);
    if (!p) return [];
    const vars = Array.isArray(p.variants) ? p.variants : [];

    const bySize = new Map();
    for (const v of vars) {
      if (!v?.size) continue;
      const key = getVariantKey(v); // size string
      if (!bySize.has(key)) {
        bySize.set(key, {
          key,
          size: String(v.size),
          sku: v.sku || null,
          stock: typeof v.stock === "number" ? v.stock : null,
        });
      }
    }

    return Array.from(bySize.values()).sort((a, b) => Number(a.size) - Number(b.size));
  };

  const computeSearch = (text) => {
    const s = norm(text);
    if (!s) return [];

    return products
      .map((p) => {
        const title = norm(p.title);
        const reference = norm(p.reference);
        const code = norm(p.code);
        const searchArr = Array.isArray(p.search) ? p.search.map((x) => norm(x)).join(" ") : "";

        // ✅ SKU + taille recherchables
        const vars = Array.isArray(p.variants) ? p.variants : [];
        const skuStr = vars.map((v) => norm(v?.sku)).join(" ");
        const sizeStr = vars.map((v) => norm(v?.size)).join(" ");

        let score = 0;

        // exact match
        if (code && code === s) score += 100;
        if (reference && reference === s) score += 90;
        if (title && title === s) score += 80;

        // contains
        if (code.includes(s)) score += 40;
        if (reference.includes(s)) score += 35;
        if (title.includes(s)) score += 30;
        if (searchArr.includes(s)) score += 15;
        if (skuStr.includes(s)) score += 25;
        if (sizeStr.includes(s)) score += 20;

        return { p, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map((x) => x.p);
  };

  useEffect(() => {
    setSearchResults(computeSearch(searchText));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, products]);

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const addLine = () => setItems((prev) => [...prev, { productId: "", variantKey: "default", qty: 1, unitCost: "" }]);
  const removeLine = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  // ✅ Appliquer produit trouvé + auto-variant:
  // - si le produit a des tailles => on met la 1ère taille
  // - sinon => default
  const applyProductToRow = (productId, rowIndex = activeRow) => {
    const vars = getVariants(productId);
    const autoVariantKey = vars.length ? vars[0].key : "default";
    updateItem(rowIndex, { productId, variantKey: autoVariantKey });
  };

  // ✅ Quand on change de produit depuis le select
  // on force un variantKey cohérent (1ère taille si existantes)
  const onProductChange = (idx, productId) => {
    if (!productId) {
      updateItem(idx, { productId: "", variantKey: "default" });
      return;
    }
    applyProductToRow(productId, idx);
  };

  // ✅ Quand on change la variante, on s’assure que c’est une valeur existante
  const onVariantChange = (idx, variantKey) => {
    const currentProductId = items[idx]?.productId;
    const vars = getVariants(currentProductId);
    const allowed = new Set(["default", ...vars.map((v) => String(v.key))]);

    updateItem(idx, { variantKey: allowed.has(String(variantKey)) ? variantKey : (vars[0]?.key || "default") });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return;

    for (const it of items) {
      if (!it.productId) return alert("Choisis un produit sur chaque ligne.");
      if (!it.qty || Number(it.qty) <= 0) return alert("Quantité invalide.");
      if (it.unitCost !== "" && Number(it.unitCost) < 0) return alert("Coût unitaire invalide.");
    }

    setSaving(true);
    try {
      await addDoc(collection(db, "receptions"), {
        supplier: supplier.trim() || null,
        ref: ref.trim() || null,
        note: note.trim() || "",
        status: "posted",
        createdAt: serverTimestamp(),
        createdBy: "admin",
        items: items.map((it) => ({
          productId: it.productId,
          variantKey: it.variantKey || "default", // taille ou default
          qty: Number(it.qty),
          unitCost: it.unitCost === "" ? null : Number(it.unitCost),
        })),
      });

      setSupplier("");
      setRef("");
      setNote("");
      setItems([{ productId: "", variantKey: "default", qty: 1, unitCost: "" }]);
      setSearchText("");
      setSearchResults([]);
      setActiveRow(0);
      alert("✅ Réception enregistrée.");
    } catch (err) {
      console.error(err);
      alert("Erreur: " + (err.message || "Impossible d'enregistrer."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container py-4">
      <style>{`
        .soft-card { border: 0; box-shadow: 0 10px 22px rgba(0,0,0,.06); border-radius: 14px; }
        .result-item:hover { background: #f8f9fa; }
      `}</style>

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h3 className="mb-0">Réception de produits</h3>
          <div className="text-muted small">
            Recherche par <b>code / référence / titre / SKU / taille</b> → remplit la <b>ligne sélectionnée</b>.
            Variante = <b>taille</b> (40, 41, 42…)
          </div>
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={addLine}>
          + Ajouter une ligne
        </button>
      </div>

      <form onSubmit={submit} className="card soft-card p-3">
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Fournisseur</label>
            <input className="form-control" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Référence (facture)</label>
            <input className="form-control" value={ref} onChange={(e) => setRef(e.target.value)} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Note</label>
            <input className="form-control" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        <hr className="my-3" />

        {/* Recherche qui remplit la ligne */}
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-6">
            <label className="form-label">Rechercher un produit</label>
            <input
              className="form-control"
              placeholder="Ex: 00002, RF00007220, RF00007220-6, 42..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              disabled={loadingProducts}
            />
            <div className="text-muted small mt-1">
              Ligne ciblée: <b>#{activeRow + 1}</b>
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Choisir la ligne à remplir</label>
            <select className="form-select" value={activeRow} onChange={(e) => setActiveRow(Number(e.target.value))}>
              {items.map((_, idx) => (
                <option key={idx} value={idx}>
                  Ligne {idx + 1}
                </option>
              ))}
            </select>
          </div>

          {searchText.trim() && (
            <div className="col-12">
              <div className="border rounded-3 p-2 bg-white">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="fw-semibold small">Résultats</div>
                  <div className="text-muted small">{searchResults.length} trouvé(s)</div>
                </div>

                {searchResults.length ? (
                  <div className="list-group mt-2">
                    {searchResults.map((p) => {
                      const vCount = getVariants(p.id).length;
                      return (
                        <button
                          type="button"
                          key={p.id}
                          className="list-group-item list-group-item-action result-item"
                          onClick={() => applyProductToRow(p.id)}
                        >
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <div>
                              <div className="fw-semibold">{p.title}</div>
                              <div className="text-muted small">
                                {p.code ? <>Code: <b>{p.code}</b> • </> : null}
                                {p.reference ? <>Réf: <b>{p.reference}</b></> : null}
                              </div>
                            </div>
                            <span className="badge text-bg-light border text-dark">
                              {vCount ? `${vCount} taille(s)` : "Aucune variante"}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-muted small mt-2">Aucun résultat.</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Table items */}
        <div className="table-responsive">
          <table className="table align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: 330 }}>Produit</th>
                <th style={{ width: 360 }}>Variante (taille)</th>
                <th style={{ width: 120 }}>Qté</th>
                <th style={{ width: 180 }}>Coût unitaire</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>

            <tbody>
              {items.map((it, idx) => {
                const p = productMap.get(it.productId);
                const vars = getVariants(it.productId);
                const hasVariants = vars.length > 0;

                return (
                  <React.Fragment key={idx}>
                    <tr
                      onClick={() => setActiveRow(idx)}
                      style={{ cursor: "pointer" }}
                      className={activeRow === idx ? "table-primary" : ""}
                    >
                      <td>
                        <select
                          className="form-select"
                          value={it.productId}
                          onChange={(e) => onProductChange(idx, e.target.value)}
                        >
                          <option value="">-- Choisir --</option>
                          {products.map((pp) => (
                            <option key={pp.id} value={pp.id}>
                              {pp.title} {pp.code ? `• ${pp.code}` : ""} {pp.reference ? `• ${pp.reference}` : ""}
                            </option>
                          ))}
                        </select>

                        {p ? (
                          <div className="text-muted small mt-1">
                            {p.code ? <>Code: <b>{p.code}</b> • </> : null}
                            {p.reference ? <>Réf: <b>{p.reference}</b></> : null}
                          </div>
                        ) : (
                          <div className="text-muted small mt-1">Choisis un produit</div>
                        )}
                      </td>

                      <td>
                        <select
                          className="form-select"
                          value={it.variantKey || "default"}
                          onChange={(e) => onVariantChange(idx, e.target.value)}
                          disabled={!it.productId}
                        >
                          <option value="default">default (sans taille)</option>
                          {vars.map((v) => (
                            <option key={v.key} value={v.key}>
                              {variantLabel(v)}
                            </option>
                          ))}
                        </select>

                        <small className="text-muted">
                          {hasVariants ? "Choisis la taille." : "Aucune variante (tailles) pour ce produit."}
                        </small>
                      </td>

                      <td>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={it.qty}
                          onChange={(e) => updateItem(idx, { qty: e.target.value })}
                        />
                      </td>

                      <td>
                        <input
                          type="number"
                          min="0"
                          className="form-control"
                          placeholder="optionnel"
                          value={it.unitCost}
                          onChange={(e) => updateItem(idx, { unitCost: e.target.value })}
                        />
                      </td>

                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeLine(idx);
                            if (activeRow === idx) setActiveRow(0);
                          }}
                          disabled={items.length === 1}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>

                    {/* Variantes visibles sous la ligne */}
                    {it.productId && (
                      <tr>
                        <td colSpan={5} className="pt-0">
                          <div className="border rounded-3 p-2 bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="fw-semibold small">Tailles disponibles</div>
                              <div className="text-muted small">{hasVariants ? `${vars.length}` : "0"}</div>
                            </div>

                            {!hasVariants ? (
                              <div className="text-muted small mt-2">Aucune taille.</div>
                            ) : (
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {vars.map((v) => (
                                  <span key={v.key} className="badge text-bg-light border text-dark">
                                    {variantLabel(v)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <button className="btn btn-primary" disabled={saving}>
          {saving ? "Enregistrement..." : "Enregistrer la réception"}
        </button>
      </form>
    </div>
  );
}
